import React, { useEffect, useMemo, useState } from "react";
import { I18nextProvider } from "react-i18next";

import createCache from "@emotion/cache";
import { CacheProvider } from "@emotion/react";
import { createTheme, CssBaseline, ThemeProvider } from "@mui/material";
import rtlPlugin from "stylis-plugin-rtl";

import i18n from "../../../i18n";
import { muiThemeOptions } from "../../../utils/mui-themes";

const ltrCache = createCache({ key: "muiltr" });
const rtlCache = createCache({ key: "muirtl", stylisPlugins: [rtlPlugin] });

const RTL_LANGUAGES: string[] = []; //["he", "ar"];

const isRtlLanguage = (lng: string) => RTL_LANGUAGES.includes(lng);

export const I18nProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isRtl, setIsRtl] = useState(() => isRtlLanguage(i18n.language));

  useEffect(() => {
    const handleLanguageChange = (lng: string) => {
      const rtl = isRtlLanguage(lng);
      setIsRtl(rtl);
      document.dir = rtl ? "rtl" : "ltr";
      document.documentElement.lang = lng;
    };

    // Apply direction for the current language on mount
    handleLanguageChange(i18n.language);

    i18n.on("languageChanged", handleLanguageChange);
    return () => {
      i18n.off("languageChanged", handleLanguageChange);
    };
  }, []);

  const theme = useMemo(() => createTheme(muiThemeOptions, { direction: isRtl ? "rtl" : "ltr" }), [isRtl]);

  return (
    <I18nextProvider i18n={i18n}>
      <CacheProvider value={isRtl ? rtlCache : ltrCache}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          {children}
        </ThemeProvider>
      </CacheProvider>
    </I18nextProvider>
  );
};
